import { useQuery } from "@tanstack/react-query";
import { router } from "expo-router";
import { observer } from "mobx-react";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { PetCard } from "~/components/V2/SpeciesCard";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BrandText } from "~/components/V2/Text/BrandText";
import { View } from "~/components/V2/View";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { assert } from "~/utils/assert";
import { bookingDataLayerPush } from "~/utils/logBookingEvent";
import { useProgressBarStep } from "./useProgressBarStep";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import { withBookingData } from "./withBookingData";

const SelectPetScreen = observer(function SelectPetScreen() {
  const store = useStore();
  const activeUser = store.authStore.activeUser;

  const { clinicId, isNewUser, isStandAloneBookingFlow } =
    useStandaloneBookingFlow();

  useProgressBarStep(2);

  assert(activeUser, "Active user should be defined");

  const query = useQuery({
    queryKey: ["myPets", activeUser.id],
    queryFn: async () => {
      const pets = await store.petsStore.getMyPets();
      return pets;
    },
  });

  if (query.isLoading) {
    return (
      <View colorWhite flex>
        <View padding32 centerContent>
          <SpinnerV2 />
        </View>
      </View>
    );
  }

  if (query.isError) {
    return (
      <View colorWhite flex>
        <View centerContent paddingHorizontal24>
          <ErrorViewV2
            error={query.error}
            title={intl.formatMessage({
              id: "OTjx7h",
              defaultMessage: "Something went wrong",
              description: "Error message title when fetching pet list fails",
            })}
          />
          <ButtonV2
            onPress={query.refetch}
            title={intl.formatMessage({
              id: "lTcTd0",
              defaultMessage: "Try Again!",
              description: "Button to retry fetching pet list",
            })}
          />
        </View>
      </View>
    );
  }

  const pets = query.data;

  assert(pets, "Pets should be defined");

  return (
    <View paddingHorizontal16 flex colorWhite>
      <View paddingVertical40 alignItemsCenter>
        <BrandText sizeH1 weightExtraBold colorBlack>
          <FormattedMessage
            id="wtJ+yD"
            defaultMessage="Choose pet"
            description="Title on the select pet screen"
          />
        </BrandText>
      </View>
      <Spacer small />
      {/* Pets list */}
      <View style={{ gap: styleConstantsV2.spacing16 }}>
        {query.data.map((pet) => {
          return (
            <PetCard
              key={pet.id}
              pet={pet}
              onPress={() => {
                store.bookingStore.setPatientId(pet.id);
                store.bookingStore.setPatientSpeciesId(pet.species?.id ?? null);

                if (isStandAloneBookingFlow) {
                  // Log the event to the booking data layer
                  bookingDataLayerPush({
                    event: "Booking_patient_select_web_app",
                    patient_id: pet.id,
                    species_id: pet.species?.id,
                    species_name: pet.species?.name,
                    breed_id: pet.breed?.id,
                    breed_name: pet.breed?.name,
                  });

                  router.navigate({
                    pathname: "/(standalone)/booking/appointment-type",
                    params: {
                      clinicId,
                      isNewUser: isNewUser ? "1" : "0",
                    },
                  });
                } else {
                  router.navigate({
                    pathname: "/(app)/book-appointment/appointment-type",
                  });
                }
              }}
            />
          );
        })}
      </View>
      <Spacer extraExtraLarge />
      <View centerContent>
        <ButtonV2
          small
          secondary
          title={intl.formatMessage({
            id: "h3bTRM",
            defaultMessage: "Book for another pet",
            description: "Button to book for another pet",
          })}
          onPress={() => {
            if (isStandAloneBookingFlow) {
              router.navigate({
                pathname: "/(standalone)/booking/add-new-pet",
                params: {
                  clinicId,
                  isNewUser: isNewUser ? "1" : "0",
                },
              });
            } else {
              router.navigate({
                pathname: "/(app)/book-appointment/add-new-pet",
              });
            }
          }}
        />
      </View>
      <Spacer extraExtraLarge />
    </View>
  );
});

// If any required data from earlier in the booking process is missing, redirect to the first step
export const SelectPetScreenWithBookingData = withBookingData(SelectPetScreen, [
  "clinicId",
]);
